import React from "react";

import cn from "@/utils/cn";

import { Spinner } from "../Spinner";

import {
  endLoadingPaddings,
  sizes,
  spinnerPaddings,
  spinnerSizes,
  startLoadingPaddings,
  variants,
} from "./utils";

export type IconsProps =
  | { startIcon: React.ReactElement; endIcon?: never }
  | { endIcon: React.ReactElement; startIcon?: never }
  | { endIcon?: undefined; startIcon?: undefined };

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  isLoading?: boolean;
} & IconsProps & { "data-testid"?: string };

/**
 * xs, sm buttons have to use 20x20 icons
 * md, lg buttons have to use 24x24 icons
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      size = "sm",
      variant = "primary",
      isLoading,
      startIcon,
      endIcon,
      className = "",
      ...props
    },
    ref,
  ) => {
    const hasNoIcons = !startIcon && !endIcon;
    const hasStartIcon = startIcon && !endIcon;
    const hasEndIcon = !startIcon && endIcon;
    return (
      <button
        ref={ref}
        className={cn(
          "relative inline-flex items-center justify-center font-sora font-regular leading-4 transition-all",
          variants[variant],
          sizes[size],
          {
            [`${startLoadingPaddings[size]}`]:
              hasStartIcon || (hasNoIcons && isLoading),
          },
          { [`${endLoadingPaddings[size]}`]: hasEndIcon },
          { "pointer-events-none": isLoading },
          className,
        )}
        data-testid="button"
        {...props}
      >
        <span className="relative w-full">
          {((hasNoIcons && isLoading) || (hasStartIcon && isLoading)) && (
            <span
              className={cn(
                "absolute inset-y-0 inline-flex -translate-x-full items-center",
                spinnerPaddings[size],
              )}
            >
              <Spinner
                size={spinnerSizes[size]}
                className="text-current"
                data-testid="button-start-spinner"
              />
            </span>
          )}
          {!isLoading && hasStartIcon && (
            <span className="absolute inset-y-0 inline-flex -translate-x-full items-center">
              {React.cloneElement(startIcon, {
                className: cn(startIcon.props.className, "fill-current"),
                "data-testid": "button-start-icon",
              })}
            </span>
          )}
          <span
            className={cn(
              { "pl-1": hasStartIcon || (hasNoIcons && isLoading) },
              { "pr-1": hasEndIcon || (hasEndIcon && isLoading) },
            )}
          >
            {children}
          </span>
          {hasEndIcon && isLoading && (
            <span
              className={cn(
                "absolute inset-y-0 inline-flex items-center",
                spinnerPaddings[size],
              )}
            >
              <Spinner
                size={spinnerSizes[size]}
                className="text-current"
                data-testid="button-end-spinner"
              />
            </span>
          )}
          {hasEndIcon && !isLoading && (
            <span className="absolute inset-y-0 inline-flex items-center text-chorus-black-300">
              {React.cloneElement(endIcon, {
                className: cn(endIcon.props.className, "fill-current"),
                "data-testid": "button-end-icon",
              })}
            </span>
          )}
        </span>
      </button>
    );
  },
);

Button.displayName = "Button";
